export interface TokenStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  deleteItem(key: string): Promise<void>;
}

export const STORAGE_KEYS = {
  MWA_AUTH_TOKEN: 'dubs_mwa_auth_token',
  MWA_WALLET_ADDRESS: 'dubs_mwa_wallet_address',
  JWT_TOKEN: 'dubs_jwt_token',
  PHANTOM_SESSION: 'dubs_phantom_session',
  PHANTOM_CONNECT_IN_FLIGHT: 'dubs_phantom_connect_in_flight',
} as const;

/**
 * Creates a TokenStorage backed by expo-secure-store.
 * Lazy-imports the module so it's only required at runtime when actually used.
 * Throws a clear error if expo-secure-store is not installed.
 */
export function createSecureStoreStorage(): TokenStorage {
  // Use `any` to avoid requiring expo-secure-store types at build time.
  // The module is lazy-imported at runtime only when this storage is actually used.
  let SecureStore: any = null;

  function getStore(): {
    getItemAsync: (key: string) => Promise<string | null>;
    setItemAsync: (key: string, value: string) => Promise<void>;
    deleteItemAsync: (key: string) => Promise<void>;
  } {
    if (!SecureStore) {
      try {
        // Use require() to keep the import opaque to TypeScript's DTS generation.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        SecureStore = require('expo-secure-store');
      } catch {
        throw new Error(
          '@dubsdotapp/expo: expo-secure-store is required for default token storage. ' +
          'Install it with: npx expo install expo-secure-store — ' +
          'or pass a custom tokenStorage prop to <DubsProvider>.',
        );
      }
    }
    return SecureStore;
  }

  return {
    async getItem(key: string): Promise<string | null> {
      const store = getStore();
      return store.getItemAsync(key);
    },
    async setItem(key: string, value: string): Promise<void> {
      const store = getStore();
      await store.setItemAsync(key, value);
    },
    async deleteItem(key: string): Promise<void> {
      const store = getStore();
      await store.deleteItemAsync(key);
    },
  };
}
